import React, { useEffect, useState } from 'react';
import { Grid, Label } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import List from '../List';
import { getVocabulary, listGroups, listUsers } from '@plone/volto/actions';

import CompactFilter from '../CompactFilter';
import CompactFilterObject from '../CompactFilter/CompactFilterObject';
import responsibilitiesSVG from '../../../icons/themen.svg';
import departmentsSVG from '../../../icons/fachbereich.svg';
import locationSVG from '../../../icons/location.svg';
import { showUserDetails } from '../../../actions';
import Avatar from '../../../customizations/volto/components/theme/Avatar/Avatar';
import { getColor } from '@plone/volto/helpers';

const defaultList = []; // avoid infinite loop (useSelector + useEffect)

const UserListingView = () => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(-1);
  const [filters, setFilters] = useState([
    new CompactFilterObject(
      'Zuständigkeit',
      'select',
      (filter, user) => {
        if (!user.zustaendigkeiten) {
          return false;
        }
        return filter.active.every((filterValue) =>
          user.zustaendigkeiten
            .map((zustaendigkeit) => zustaendigkeit)
            .includes(filterValue),
        );
      },
      responsibilitiesSVG,
    ),
    new CompactFilterObject(
      'Fachbereich',
      'select',
      (filter, user) =>
        user.fachbereich
          .map((fachbereich) => fachbereich.id)
          .some((r) => filter.active.indexOf(r) >= 0),
      departmentsSVG,
    ),
    new CompactFilterObject(
      'Standort',
      'select',
      (filter, user) => filter.active.includes(user.standort),
      locationSVG,
    ),
  ]);
  const { responsibilities, users, departments, locations } = useSelector(
    (state) => {
      return {
        responsibilities:
          state.vocabularies?.[
            'hornundhorn.contenttypes.vocabularies.metadata_responsibilities'
          ] ?? defaultList,
        users: state.users?.users ?? defaultList,
        departments: state.groups?.groups ?? defaultList,
        locations:
          state.vocabularies?.[
            'hornundhorn.contenttypes.vocabularies.metadata_location'
          ] ?? defaultList,
      };
    },
  );
  const columns = {
    fullname: {
      label: users.length + ' Personen',
      cellComponent: (entry) => {
        return (
          <Grid>
            <Grid.Column width={3}>
              {entry.portrait ? (
                // eslint-disable-next-line no-restricted-syntax -- portrait comes as a ready URL
                <img
                  className={'user-portrait ui image'}
                  alt={entry.id}
                  src={entry.portrait}
                ></img>
              ) : (
                <Avatar
                  src={entry.portrait}
                  title={entry.fullname || entry.id}
                  color={getColor(entry.id)}
                />
              )}
            </Grid.Column>
            <Grid.Column width={9}>
              <Grid.Column
                className={'user-fullname'}
                title={entry.fullname || entry.id}
                onClick={() => {
                  dispatch(showUserDetails(entry.id));
                }}
              >
                {entry.fullname}
              </Grid.Column>
              <Grid.Column className={'user-kuerzel'}>
                {entry.kuerzel}
              </Grid.Column>
            </Grid.Column>
          </Grid>
        );
      },
      sortable: true,
    },
    fachbereich: {
      label: 'Fachbereich',
      cellComponent: (entry) => {
        return (
          <>
            {entry.fachbereich.map((department, departmentIndex) => {
              return (
                <Label
                  className={'department-label'}
                  key={departmentIndex}
                  style={{ background: department.farbe }}
                >
                  {department.kuerzel}
                </Label>
              );
            })}
          </>
        );
      },
    },
    email: {
      label: 'Email',
    },
    telefon: {
      label: 'Telefon',
      cellComponent: (entry) => {
        return <a href={'tel:' + entry?.telefon}>{entry?.telefon}</a>;
      },
    },
    durchwahl: {
      label: '',
    },
    standort: {
      label: 'Standort',
    },
  };

  useEffect(() => {
    dispatch(listUsers());
    dispatch(listGroups());
    dispatch(
      getVocabulary({
        vocabNameOrURL:
          'hornundhorn.contenttypes.vocabularies.metadata_location',
        size: 1000,
      }),
    );
    dispatch(
      getVocabulary({
        vocabNameOrURL:
          'hornundhorn.contenttypes.vocabularies.metadata_responsibilities',
        size: 1000,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilters((prev) => {
      return prev.map((elem) => {
        if (elem.title === 'Zuständigkeit') {
          if (responsibilities?.items) {
            elem.all =
              responsibilities?.items.map((item) => {
                return { label: item.label, value: item.label };
              }) ?? [];
          }
        }
        if (elem.title === 'Fachbereich') {
          elem.all = departments
            ?.filter((item) => item.id.includes('department_'))
            .map((item) => {
              return { label: item.title, value: item.id };
            });
        }
        if (elem.title === 'Standort') {
          if (locations?.items) {
            elem.all =
              locations?.items.map((item) => {
                return { label: item.label, value: item.label };
              }) ?? [];
          }
        }
        return elem;
      });
    });
  }, [responsibilities, departments, locations]);

  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    // no filter selected
    if (filters.every((filter) => filter.active.length === 0)) {
      setFilteredUsers(users);
    }

    let tmpfilteredUsers = users;

    // filter items
    filters.forEach((filter) => {
      if (filter.active.length > 0) {
        tmpfilteredUsers = tmpfilteredUsers.filter((user) =>
          filter.func(filter, user),
        );
      }
    });

    setFilteredUsers(tmpfilteredUsers);
  }, [filters, users]);

  return (
    <>
      <CompactFilter
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        filter={filters}
        updateFilter={(filter) =>
          setFilters((prev) =>
            prev.map((filterItem) => {
              if (filter.title === filterItem.title) {
                return filter;
              }
              return filterItem;
            }),
          )
        }
      />
      {users ? (
        <>
          <List
            headTitle={'Alle Standorte'}
            headColor={'#40d4e3'}
            itemColumns={columns}
            items={filteredUsers}
            batched={170}
            pagination={false}
            sizeOptions={[1]}
            type={'user'}
            sortColumn={'fullname'}
            defaultCollapsed={false}
            sortDir={'asc'}
          />
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default UserListingView;
